import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar as CalendarIcon, ShieldCheck, X, CheckCircle2, Hash, Lock, Zap, Shield, FolderPlus, Crown, Target, Info } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { cn } from '../lib/utils';

export default function CreateGoal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    target_count: 5,
    target_unit: 'times',
    end_date: addDays(new Date(), 7),
    group_id: searchParams.get('group_id') || '',
    tracking_type: 'binary',
  });
  const [groups, setGroups] = useState([]);
  
  // New group creation state (Pro feature)
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  
  const isPremium = user?.is_premium;

  // Calculate max target based on days until end date
  const daysUntilEnd = Math.max(1, Math.ceil((formData.end_date - new Date()) / (1000 * 60 * 60 * 24)));
  const maxTarget = Math.max(30, daysUntilEnd);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Only load groups for Pro users, otherwise set empty array
        if (isPremium) {
          const groupsRes = await api.get('/api/groups');
          setGroups(groupsRes.data);
          
          if (!formData.group_id && groupsRes.data.length > 0) {
            const general = groupsRes.data.find(g => g.name === 'General') || groupsRes.data[0];
            setFormData(prev => ({ ...prev, group_id: general.id }));
          }
        }
      } catch (e) {
        console.error("Failed to fetch data", e);
      }
    };
    loadData();
  }, [isPremium]);

  // Create new group (Pro feature)
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    setCreatingGroup(true);
    try {
      const res = await api.post('/api/groups', { name: newGroupName, order: groups.length });
      setGroups([...groups, res.data]);
      setFormData(prev => ({ ...prev, group_id: res.data.id }));
      setNewGroupName('');
      setShowNewGroupDialog(false);
      toast.success('Group created!');
    } catch (e) {
      toast.error('Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title) {
        toast.error("Please complete all fields");
        return;
    }

    setLoading(true);
    try {
      const payload = {
          ...formData,
          end_date: formData.end_date.toISOString(),
          penalty_amount: 0
      }
      await api.post('/api/goals', payload);
      toast.success("Goal Created!");
      navigate('/dashboard');
    } catch (e) {
      toast.error("Failed to create goal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn(
      "min-h-screen",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      
      <div className="flex items-center justify-center p-4 md:p-6 min-h-[calc(100vh-64px)]">
        <Card className={cn(
          "w-full max-w-lg relative",
          isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-xl"
        )}>
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute right-4 top-4 h-8 w-8 rounded-full z-10",
            isDark ? "text-zinc-400 hover:text-white hover:bg-white/10" : "text-[#9E8778] hover:text-[#1A1A1A] hover:bg-slate-100"
          )}
          onClick={() => navigate('/dashboard')}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <CardHeader className="pr-12">
          <CardTitle className={cn("text-2xl", isDark ? "text-gradient" : "text-[#1A1A1A]")}>New Goal Contract</CardTitle>
          <CardDescription className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Step {step} of 2</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Goal Title</Label>
                <Input 
                  placeholder="e.g. Gym 5x this week" 
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  className={cn(
                    "text-lg",
                    isDark ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" : "bg-white border-[#E5E2DA] text-[#1A1A1A] placeholder:text-[#9E8778]"
                  )}
                  data-testid="goal-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                  {/* Group Selection - Pro Feature */}
                  <div className="space-y-2">
                      <Label className={cn("flex items-center gap-2", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>
                        Group
                        {!isPremium && (
                          <Badge variant="outline" className="text-xs border-coral/50 text-coral">
                            <Crown className="w-3 h-3 mr-1" />
                            Pro
                          </Badge>
                        )}
                      </Label>
                      {isPremium ? (
                        <div className="flex gap-2">
                          <Select value={formData.group_id} onValueChange={v => setFormData({...formData, group_id: v})}>
                              <SelectTrigger className={cn("flex-1", isDark ? "bg-zinc-800 border-zinc-700 text-zinc-300" : "bg-white border-[#E5E2DA] text-[#1A1A1A]")}>
                                  <SelectValue placeholder="Select Group" />
                              </SelectTrigger>
                              <SelectContent className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#E5E2DA]"}>
                                  {groups.map(g => (
                                      <SelectItem key={g.id} value={g.id} className={isDark ? "text-zinc-300 focus:text-white focus:bg-zinc-800" : "text-[#1A1A1A] focus:text-slate-900 focus:bg-[#F4F3EE]"}>{g.name}</SelectItem>
                                  ))}
                              </SelectContent>
                          </Select>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="icon"
                            className={isDark ? "border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800" : "border-[#E5E2DA] text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-[#F4F3EE]"}
                            onClick={() => setShowNewGroupDialog(true)}
                            title="Create new group"
                          >
                            <FolderPlus className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors",
                            isDark ? "bg-zinc-800/50 border-zinc-700 hover:border-coral/50" : "bg-slate-50 border-[#E5E2DA] hover:border-amber-400"
                          )}
                          onClick={() => navigate('/upgrade')}
                        >
                          <Lock className={isDark ? "w-4 h-4 text-zinc-500" : "w-4 h-4 text-[#9E8778]"} />
                          <span className={cn("text-sm", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>Upgrade to organize goals</span>
                        </div>
                      )}
                  </div>
                  
                  <div className="space-y-2">
                      <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Tracking Type</Label>
                      <Select value={formData.tracking_type} onValueChange={v => setFormData({...formData, tracking_type: v})}>
                          <SelectTrigger className={isDark ? "bg-zinc-800 border-zinc-700 text-zinc-300" : "bg-white border-[#E5E2DA] text-[#1A1A1A]"}>
                              <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#E5E2DA]"}>
                              <SelectItem value="binary" className={isDark ? "text-zinc-300 focus:text-white focus:bg-zinc-800" : "text-[#1A1A1A] focus:text-slate-900 focus:bg-[#F4F3EE]"}>
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                  <span>Check (Yes/No)</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="count" className={isDark ? "text-zinc-300 focus:text-white focus:bg-zinc-800" : "text-[#1A1A1A] focus:text-slate-900 focus:bg-[#F4F3EE]"}>
                                <div className="flex items-center gap-2">
                                  <Hash className="w-4 h-4 text-coral" />
                                  <span>Units (Numbers)</span>
                                </div>
                              </SelectItem>
                          </SelectContent>
                      </Select>
                  </div>
              </div>
              
              {/* Tracking Type Examples - Info Box */}
              <div className={cn(
                "border rounded-lg p-3",
                isDark ? "bg-zinc-800/50 border-zinc-700" : "bg-[#FDF0EC]/50 border-[#F5E9DE]"
              )}>
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-coral mt-0.5 flex-shrink-0" />
                  <div className={cn("text-xs", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                    {formData.tracking_type === 'binary' ? (
                      <>
                        <p className={cn("font-medium mb-1", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>Check Tracking</p>
                        <p>Log once per day with a simple Yes/No. Perfect for habits like:</p>
                        <ul className={cn("mt-1 space-y-0.5", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                          <li>• &ldquo;Meditate daily&rdquo; → Check off each day</li>
                          <li>• &ldquo;No social media&rdquo; → Mark success each day</li>
                          <li>• &ldquo;Wake up at 6am&rdquo; → Yes or No daily</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className={cn("font-medium mb-1", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>Units Tracking</p>
                        <p>Log a number each time to track cumulative progress:</p>
                        <ul className={cn("mt-1 space-y-0.5", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                          <li>• &ldquo;Read 10 books&rdquo; → Log 1 each book finished</li>
                          <li>• &ldquo;Run 50 miles&rdquo; → Log miles per run</li>
                          <li>• &ldquo;100 pushups daily&rdquo; → Log count each session</li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Target Section */}
              {formData.tracking_type === 'binary' ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                      <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Target Days</Label>
                      <span className="text-coral font-bold">{formData.target_count} days</span>
                  </div>
                  <Slider 
                    value={[formData.target_count]} 
                    onValueChange={v => setFormData({...formData, target_count: v[0]})}
                    min={1} max={maxTarget} step={1}
                  />
                  <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>
                    Check off {formData.target_count} days before deadline ({daysUntilEnd} days available)
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Target Value</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.target_count}
                        onChange={e => setFormData({...formData, target_count: parseInt(e.target.value) || 1})}
                        className={isDark ? "bg-zinc-800 border-zinc-700 text-white" : "bg-white border-[#E5E2DA] text-[#1A1A1A]"}
                        placeholder="e.g. 10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Unit</Label>
                      <Input
                        value={formData.target_unit}
                        onChange={e => setFormData({...formData, target_unit: e.target.value})}
                        className={isDark ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" : "bg-white border-[#E5E2DA] text-[#1A1A1A] placeholder:text-[#9E8778]"}
                        placeholder="times, books, miles..."
                      />
                    </div>
                  </div>
                  <p className={cn("text-xs", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>
                    Reach {formData.target_count} {formData.target_unit} by {format(formData.end_date, 'MMM d, yyyy')}
                  </p>
                </div>
              )}

               <div className="space-y-2 flex flex-col">
                <Label className={cn("mb-2", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>Target End Date</Label>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant={"outline"}
                            className={cn(
                                "w-full justify-start text-left font-normal",
                                isDark ? "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700" : "bg-white border-[#E5E2DA] text-[#1A1A1A] hover:bg-[#F4F3EE]",
                                !formData.end_date && (isDark ? "text-zinc-500" : "text-[#9E8778]")
                            )}
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.end_date ? format(formData.end_date, "PPP") : <span>Pick a date</span>}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className={cn("w-auto p-0", isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#E5E2DA]")} align="start">
                        <Calendar
                            mode="single"
                            selected={formData.end_date}
                            onSelect={(date) => setFormData({...formData, end_date: date})}
                            initialFocus
                            disabled={(date) => date < new Date()}
                        />
                    </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4">
              <div className="text-center space-y-4 py-8">
                <div className={cn("p-6 rounded-full w-fit mx-auto", isDark ? "bg-zinc-800" : "bg-[#FDF0EC]")}>
                  <ShieldCheck className="w-16 h-16 text-coral" />
                </div>
                <h3 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>Ready to commit?</h3>
                <p className={cn("max-w-xs mx-auto", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                  You're committing to this goal on your honor. Show up every day and keep your word.
                </p>
              </div>
            </div>
          )}

        </CardContent>
        <CardFooter className="flex justify-between">
            {step > 1 && <Button variant="ghost" onClick={() => setStep(step - 1)} className={isDark ? "text-zinc-400 hover:text-white hover:bg-white/10" : "text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-slate-100"}>Back</Button>}
            <div className="ml-auto">
                {step < 2 ? (
                    <Button onClick={() => setStep(step + 1)} className="btn-glossy">Next Step</Button>
                ) : (
                    <Button
                      onClick={handleCreate}
                      className="btn-glossy font-bold"
                      disabled={loading}
                      data-testid="commit-btn"
                    >
                        {loading ? 'Processing...' : 'COMMIT TO GOAL'}
                    </Button>
                )}
            </div>
        </CardFooter>
      </Card>
      </div>
      
      {/* New Group Dialog (Pro Feature) */}
      <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
        <DialogContent className={isDark ? "bg-zinc-900 border-zinc-800 max-w-sm" : "bg-white border-[#E5E2DA] max-w-sm"}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
              <FolderPlus className="w-5 h-5 text-coral" />
              Create New Group
            </DialogTitle>
            <DialogDescription className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>
              Organize your goals by creating custom groups
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className={cn("mb-2 block", isDark ? "text-zinc-300" : "text-[#1A1A1A]")}>Group Name</Label>
            <Input
              placeholder="e.g. Fitness, Learning, Work..."
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateGroup()}
              className={isDark ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500" : "bg-white border-[#E5E2DA] text-[#1A1A1A] placeholder:text-[#9E8778]"}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowNewGroupDialog(false)} 
              className={isDark ? "border-zinc-700 text-zinc-300 hover:bg-zinc-800" : "border-[#E5E2DA] text-[#5F5F5F] hover:bg-[#F4F3EE]"}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGroup} 
              disabled={creatingGroup || !newGroupName.trim()}
              className="btn-glossy"
            >
              {creatingGroup ? 'Creating...' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
